import { FormEvent, useState, type ReactNode } from "react";
import { LOCALE_HEADER } from "../../src/i18n/locales";
import { msg } from "../../src/i18n/translate";
import { TEACHER_FEEDBACK_CATEGORIES } from "../../src/feedback-tickets";
import { ScreenshotPicker } from "./feedback-screenshots";
import { locale, t } from "./i18n";

// LINE is a Taiwan channel: only the Chinese site links to it (English uses email and the form).
export const LINE_URL = "https://line.me/R/ti/p/@551vunlv";

type ContactKind = "public" | "teacher";

// `/contact?topic=school` (the pricing page's school plan) opens on 學校詢價.
const publicTopic = () =>
  typeof location !== "undefined" && new URLSearchParams(location.search).get("topic") === "school" ? msg("學校詢價") : msg("需求諮詢");

/** Teachers' form also takes screenshots and the open class; the Worker stores it as a ticket (D-101). */
const DRAFT_KEY = "classmori-feedback-draft";

/**
 * `hints` renders under the message while the teacher types (D-171: help suggestions). It only shows things: the
 * submit button depends on nothing but `busy`, so a suggestion can never stop real feedback from being sent.
 */
export function ContactForm({ kind, classId, onDone, hints }: { kind: ContactKind; classId?: string; onDone?: (ticketId?: string) => void; hints?: (draft: { category: string; message: string }) => ReactNode }) {
  const [category, setCategory] = useState<string>(() => kind === "public" ? publicTopic() : msg("使用問題"));
  const [email, setEmail] = useState("");
  // A teacher's unsent feedback survives opening a suggested page and coming back (D-171); this tab only.
  const [message, setMessageState] = useState(() => { if (kind === "public") return ""; try { return sessionStorage.getItem(DRAFT_KEY) ?? ""; } catch { return ""; } });
  const setMessage = (value: string) => {
    setMessageState(value);
    if (kind !== "public") try { if (value) sessionStorage.setItem(DRAFT_KEY, value); else sessionStorage.removeItem(DRAFT_KEY); } catch { /* storage blocked */ }
  };
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [screenshots, setScreenshots] = useState<File[]>([]);
  const isPublic = kind === "public";

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setStatus("");
    try {
      let body: BodyInit = JSON.stringify({ category, email, message, website: "" });
      if (!isPublic) {
        const form = new FormData();
        for (const [name, value] of Object.entries({ category, email, message, website: "", classId: classId ?? "" })) form.set(name, value);
        for (const file of screenshots) form.append("screenshots", file);
        body = form;
      }
      const response = await fetch(isPublic ? "/api/contact" : "/api/feedback", {
        method: "POST",
        headers: { ...(isPublic ? { "content-type": "application/json" } : {}), [LOCALE_HEADER]: locale },
        body,
      });
      const result = await response.json().catch(() => ({ ok: false })) as { ok: boolean; data?: { ticketId?: string }; error?: { message: string } };
      if (!result.ok) throw new Error(result.error?.message?.split("。／")[0] || t("送出失敗，請稍後再試。"));
      setMessage("");
      setEmail("");
      setScreenshots([]);
      setStatus(isPublic ? t("已收到你的訊息，我們會儘快查看。如需回覆，我們會寄到你留下的信箱。") : t("已收到你的回饋。我們回覆時會寄信通知你，也可以在「我的回饋」查看進度。"));
      onDone?.(result.data?.ticketId);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : t("送出失敗，請稍後再試。"));
    } finally {
      setBusy(false);
    }
  }

  return <form className="contact-form" onSubmit={submit}>
    <label>{t("想聊什麼？")}<select value={category} onChange={(event) => setCategory(event.target.value)}>
      {// The Worker validates the Chinese category keys; only the label is translated.
      (isPublic ? [msg("需求諮詢"), msg("產品問題"), msg("學校詢價"), msg("功能建議")] : TEACHER_FEEDBACK_CATEGORIES).map((item) => <option key={item} value={item}>{t(item)}</option>)}
    </select></label>
    <label>{isPublic ? t("電子郵件（必填）") : t("回覆信箱（選填，預設使用登入信箱）")}
      <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required={isPublic} maxLength={254} autoComplete="email" placeholder="name@example.com" />
    </label>
    <label>{t("你的訊息")}<textarea value={message} onChange={(event) => setMessage(event.target.value)} required minLength={5} maxLength={3000} rows={6} placeholder={isPublic ? t("告訴我們你的需求、問題或想法…") : t("遇到什麼問題？或希望我們改善什麼？")} /></label>
    {hints?.({ category, message })}
    {isPublic
      ? <p className="contact-privacy">{t("請勿填寫學生姓名、班級名冊或密碼。送出後我們會用於回覆與處理這次聯絡。")}</p>
      : <>
        <ScreenshotPicker files={screenshots} onChange={setScreenshots} disabled={busy} />
      </>}
    <button className="button primary" type="submit" disabled={busy}>{busy ? t("送出中…") : t("送出訊息")}</button>
    {status && <p className="contact-status" role="status" aria-live="polite">{status}</p>}
  </form>;
}

export function LineButton() {
  return <a className="line-float" href={LINE_URL} target="_blank" rel="noopener noreferrer" aria-label={t("用 LINE 與小森課堂聊聊")} title={t("用 LINE 聊聊")}><span aria-hidden="true">LINE</span><b>{t("聊聊")}</b></a>;
}
